import React from 'react';

export type InvitationTextBlock = { type: 'p'; text: string } | { type: 'ul'; items: string[] };

/**
 * Řádek začínající pomlčkou → položky seznamu. Na jednom řádku lze psát „- a - b - c“ (oddělené mezerami kolem pomlček).
 * Ostatní řádky → odstavce s zachovanými odřádkováními (whitespace-pre-wrap).
 * Prázdný řádek oddělí bloky.
 */
export function parseInvitationFormattedBlocks(text: string): InvitationTextBlock[] {
  const raw = text.replace(/\r\n/g, '\n');
  const lines = raw.split('\n');
  const blocks: InvitationTextBlock[] = [];
  let currentUl: string[] = [];
  let currentP: string[] = [];

  const flushP = () => {
    if (currentP.length > 0) {
      const t = currentP.join('\n').trimEnd();
      if (t.length > 0) blocks.push({ type: 'p', text: t });
      currentP = [];
    }
  };

  const flushUl = () => {
    if (currentUl.length > 0) {
      blocks.push({ type: 'ul', items: [...currentUl] });
      currentUl = [];
    }
  };

  const bulletItemsFromLine = (line: string): string[] | null => {
    const m = line.match(/^\s*-\s*(.*)$/);
    if (!m) return null;
    const rest = m[1].trim();
    if (!rest) return [''];
    const parts = rest.split(/\s+-\s+/).map((s) => s.trim()).filter((s) => s.length > 0);
    return parts.length > 0 ? parts : [rest];
  };

  for (const line of lines) {
    if (line.trim() === '') {
      flushUl();
      flushP();
      continue;
    }
    const items = bulletItemsFromLine(line);
    if (items) {
      flushP();
      currentUl.push(...items);
    } else {
      flushUl();
      currentP.push(line);
    }
  }
  flushUl();
  flushP();

  return blocks;
}

const defaultBodyClass = 'text-[#666] text-sm sm:text-base leading-relaxed';

export function InvitationFormattedText({
  text,
  className = '',
  bodyClassName = defaultBodyClass,
  justify = false,
  compact = false,
}: {
  text: string;
  /** Wrapper (např. zarovnání) */
  className?: string;
  /** Třídy pro odstavce a položky seznamu */
  bodyClassName?: string;
  /** Zarovnání odstavců do bloku (seznamy zůstávají vlevo) */
  justify?: boolean;
  /** Kompaktnější seznamy a mezery (např. karty občerstvení na pozvánce) */
  compact?: boolean;
}) {
  const blocks = parseInvitationFormattedBlocks(text.trim());
  if (blocks.length === 0) return null;

  const justifyCls = justify ? 'text-justify hyphens-auto' : '';
  const ulSpacing = compact
    ? 'pl-4 sm:pl-5 space-y-0 mb-1 last:mb-0'
    : 'pl-5 sm:pl-6 space-y-1 mb-2 last:mb-0';
  const pSpacing = compact ? 'mb-1 last:mb-0' : 'mb-2 last:mb-0';

  return (
    <div className={className}>
      {blocks.map((b, i) =>
        b.type === 'ul' ? (
          <ul
            key={i}
            className={`${bodyClassName} list-disc ${ulSpacing} marker:text-[#b70b9d] text-left`}
          >
            {b.items.map((item, j) => (
              <li key={j} className={compact ? 'pl-0' : 'pl-0.5'}>
                {item}
              </li>
            ))}
          </ul>
        ) : (
          <p key={i} className={`${bodyClassName} whitespace-pre-wrap ${pSpacing} ${justifyCls}`}>
            {b.text}
          </p>
        ),
      )}
    </div>
  );
}
